/**
 * Formats a number with optional SI suffixes (K, M, B) for compact display
 *
 * @param {number|null|undefined} value - The number to format
 * @param {boolean} [compact=true] - Whether to use compact notation (K/M/B suffixes)
 * @returns {string} Formatted number string
 *
 * Rounding rules (for compact values >= 1,000):
 *   1,000 – 99,999    → 1 decimal (nearest 100)   e.g. 1.2K, 12.3K, 99.5K
 *   100,000 – 999,999 → 0 decimals (nearest 1,000) e.g. 100K, 847K
 *   1,000,000+        → 2 decimals (nearest 10,000) e.g. 1.07M, 23.65M, 1.07B
 *
 * @example
 * formatNumber(847)            // "847"
 * formatNumber(1234)           // "1.2K"
 * formatNumber(9500)           // "9.5K"
 * formatNumber(12345)          // "12.3K"
 * formatNumber(99500)          // "99.5K"
 * formatNumber(100000)         // "100K"
 * formatNumber(847000)         // "847K"
 * formatNumber(1066277)        // "1.07M"
 * formatNumber(2400000)        // "2.4M"
 * formatNumber(1066000000)     // "1.07B"
 * formatNumber(1500000000)     // "1.5B"
 * formatNumber(1234567, false) // "1,234,567"
 */
export function formatNumber (value, compact = true) {
	// Handle null, undefined, or invalid values
	if (value == null || isNaN(value)) {
		return '0'
	}

	const num = Number(value)

	// Handle zero
	if (num === 0) {
		return '0'
	}

	// Non-compact formatting - use locale string with thousands separators
	if (!compact) {
		return num.toLocaleString('en-GB')
	}

	// Compact formatting with SI suffixes
	const absNum = Math.abs(num)
	const sign = num < 0 ? '-' : ''

	let formatted = ''

	if (absNum >= 1000000000) {
		formatted = formatWithSuffix(absNum / 1000000000, 'B', 2)
	} else if (absNum >= 1000000) {
		const millions = absNum / 1000000
		const rounded = Math.round(millions * 100) / 100

		// Check if rounding pushed us into billions territory
		if (rounded >= 1000) {
			formatted = formatWithSuffix(rounded / 1000, 'B', 2)
		} else {
			formatted = formatWithSuffix(millions, 'M', 2)
		}
	} else if (absNum >= 1000) {
		const thousands = absNum / 1000

		// Check if rounding would push us into millions territory
		if (Math.round(thousands) >= 1000) {
			formatted = formatWithSuffix(Math.round(thousands) / 1000, 'M', 2)
		} else if (thousands < 100) {
			formatted = formatWithSuffix(thousands, 'K', 1)
		} else {
			// 100K–999K: no decimals (nearest 1,000)
			formatted = Math.round(thousands) + 'K'
		}
	} else if (absNum >= 1) {
		// 1-999: Round to nearest integer
		formatted = String(Math.round(absNum))
	} else {
		// Less than 1: Round to 1 decimal place to avoid showing "0"
		const rounded = Math.round(absNum * 10) / 10
		formatted = rounded.toString()
	}

	return sign + formatted
}

/**
 * Rounds a number to a given number of decimal places, strips trailing
 * zeros, and appends a suffix.
 *
 * @param {number} num - The number to format (already scaled to its unit)
 * @param {string} suffix - The suffix to append (K, M, or B)
 * @param {number} decimals - Number of decimal places to round to
 * @returns {string} Formatted number with suffix
 * @private
 */
function formatWithSuffix (num, suffix, decimals) {
	const factor = 10 ** decimals
	const rounded = Math.round(num * factor) / factor
	return rounded.toFixed(decimals).replace(/\.?0+$/, '') + suffix
}
